/**
 * Which event is TODAY's, for the Calendar's focus card.
 *
 * The card used to take the first event of the server's `active` bucket.
 * Two things made that wrong on 2026-09-05 (owner report): the server draws
 * its bucket boundary on its own clock, so a fest that had opened at 10 a.m.
 * on the device still sat in `upcoming`; and the bucket is not ordered by
 * relevance, so once Mega Ascension ended the four-week "Perfect Mewtwo
 * Timed Research" moved to the front and took the card, with GO Fest live
 * underneath it.
 *
 * So the pick is made HERE, by the device clock, across both buckets:
 *
 *   1. Events running right now (floating GO wall-clock, via eventRecency).
 *   2. Background items never take the card: seasons, GO Pass, the league
 *      schedule, and anything running longer than a week (a month-long
 *      research is context, not today's event).
 *   3. The shortest remaining candidate wins: a two-day fest beats a
 *      week-long raid rotation.
 *   4. Nothing qualifying: the soonest event still to start (not a
 *      background item), else the first event not yet over, feed order.
 *
 * Pure, so the rule is unit-tested against the real feed shapes.
 */
import { eventEndMs, eventStartMs, isEventOver } from "./eventRecency";

export interface FocusEventLike {
  id: string;
  category?: string | null;
  start?: string | null;
  end?: string | null;
}

/** Categories that describe the season, not a day. */
const BACKGROUND_CATEGORIES: ReadonlySet<string> = new Set(["season", "go-pass", "league"]);

/** Longer than this and an event is background, however live it is. */
export const TODAY_FOCUS_MAX_DURATION_MS = 8 * 24 * 60 * 60 * 1000;

function durationMs(ev: FocusEventLike): number {
  return eventEndMs(ev) - eventStartMs(ev);
}

export function isTodayFocusCandidate(ev: FocusEventLike, now: number): boolean {
  if (BACKGROUND_CATEGORIES.has(ev.category ?? "")) return false;
  const start = eventStartMs(ev);
  const end = eventEndMs(ev);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return false;
  if (end - start > TODAY_FOCUS_MAX_DURATION_MS) return false;
  return start <= now && now < end;
}

export function pickTodayFocus<E extends FocusEventLike>(
  active: readonly E[],
  upcoming: readonly E[],
  now: number = Date.now(),
): E | null {
  const seen = new Set<string>();
  const all: E[] = [];
  for (const ev of [...active, ...upcoming]) {
    if (seen.has(ev.id)) continue;
    seen.add(ev.id);
    all.push(ev);
  }
  const running = all.filter((ev) => isTodayFocusCandidate(ev, now));
  if (running.length > 0) {
    // Stable: equal durations keep the feed's order.
    return [...running].sort((a, b) => durationMs(a) - durationMs(b))[0];
  }
  const soonest = all
    .filter((ev) => !BACKGROUND_CATEGORIES.has(ev.category ?? "") && eventStartMs(ev) > now)
    .sort((a, b) => eventStartMs(a) - eventStartMs(b))[0];
  return soonest ?? all.find((ev) => !isEventOver(ev, now)) ?? null;
}
